import React, { useEffect, useState, useCallback } from 'react';
import { temperatureApi, savedItemsApi } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Thermometer, RefreshCw, Plus, CalendarIcon, AlertTriangle, CheckCircle2,
    Bluetooth, BluetoothConnected, BluetoothOff, Loader2, Barcode, History,
    WifiOff, ScanLine
} from 'lucide-react';
import { toast } from 'sonner';
import { getTodayDate, formatDate, formatDateTime, cn } from '../lib/utils';
import { format } from 'date-fns';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';
import { useInkbirdThermometer } from '../hooks/useInkbirdThermometer';

const locations = [
    { id: 'fridge', label: 'Fridge', range: '0-5°C' },
    { id: 'freezer', label: 'Freezer', range: '-18°C or below' },
    { id: 'hot_hold', label: 'Hot Hold', range: '63°C or above' },
    { id: 'delivery', label: 'Delivery', range: '8°C or below' },
];

function isTemperatureAcceptable(temp, location) {
    switch (location?.toLowerCase()) {
        case 'fridge': return temp >= 0 && temp <= 5;
        case 'freezer': return temp <= -18;
        case 'hot_hold': case 'hot hold': return temp >= 63;
        case 'delivery': return temp <= 8;
        default: return true;
    }
}

function getTemperatureRange(location) {
    switch (location?.toLowerCase()) {
        case 'fridge': return '0-5°C';
        case 'freezer': return '-18°C or below';
        case 'hot_hold': case 'hot hold': return '63°C or above';
        case 'delivery': return '8°C or below';
        default: return 'N/A';
    }
}

const Temperature = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(getTodayDate());
    const [activeTab, setActiveTab] = useState('today');
    const [savedItems, setSavedItems] = useState([]);

    // History state
    const [historyData, setHistoryData] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyStartDate, setHistoryStartDate] = useState(() => {
        const d = new Date(); d.setDate(d.getDate() - 7);
        return d.toISOString().split('T')[0];
    });
    const [historyEndDate, setHistoryEndDate] = useState(getTodayDate());
    const [historyItemFilter, setHistoryItemFilter] = useState('all');

    // Form state
    const [itemName, setItemName] = useState('');
    const [location, setLocation] = useState('');
    const [temperature, setTemperature] = useState('');
    const [correctiveAction, setCorrectiveAction] = useState('');
    const [selectedSavedItemId, setSelectedSavedItemId] = useState('');

    // BLE Thermometer
    const thermometer = useInkbirdThermometer();

    // Barcode Scanner - active when dialog is open
    const handleBarcodeScan = useCallback(async (barcode) => {
        if (!createDialogOpen) return;
        try {
            const { data } = await savedItemsApi.lookupByBarcode(barcode);
            setItemName(data.name);
            setLocation(data.location_type);
            setSelectedSavedItemId(data.id);
            toast.success(`Scanned: ${data.name}`);
        } catch {
            toast.error(`Item not found for barcode: ${barcode}`);
        }
    }, [createDialogOpen]);

    useBarcodeScanner({ onScan: handleBarcodeScan, enabled: createDialogOpen });

    // No auto-populate — user clicks "Use Reading" to capture the current value

    useEffect(() => { loadLogs(); }, [selectedDate]);

    useEffect(() => {
        savedItemsApi.getAll().then(({ data }) => setSavedItems(data)).catch(() => {});
    }, []);

    const loadLogs = async () => {
        setLoading(true);
        try {
            const response = await temperatureApi.getAll({ date: selectedDate });
            setLogs(response.data);
        } catch {
            toast.error('Failed to load temperature logs');
        } finally {
            setLoading(false);
        }
    };

    const loadHistory = async () => {
        setHistoryLoading(true);
        try {
            const params = { start_date: historyStartDate, end_date: historyEndDate };
            if (historyItemFilter !== 'all') params.saved_item_id = historyItemFilter;
            const { data } = await temperatureApi.getHistory(params);
            setHistoryData(data);
        } catch {
            toast.error('Failed to load history');
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'history') loadHistory();
    }, [activeTab]);

    const handleSavedItemSelect = (itemId) => {
        setSelectedSavedItemId(itemId);
        const item = savedItems.find(i => i.id === itemId);
        if (item) {
            setItemName(item.name);
            setLocation(item.location_type);
        }
    };

    const handleCreateLog = async () => {
        if (!itemName || !location || temperature === '') {
            toast.error('Please fill in all required fields');
            return;
        }
        const temp = parseFloat(temperature);
        if (isNaN(temp)) {
            toast.error('Please enter a valid temperature');
            return;
        }
        const acceptable = isTemperatureAcceptable(temp, location);
        try {
            const response = await temperatureApi.create({
                item_name: itemName,
                location,
                temperature: temp,
                unit: 'celsius',
                is_acceptable: acceptable,
                corrective_action: !acceptable ? correctiveAction : null,
                saved_item_id: selectedSavedItemId || null,
            });
            setLogs([response.data, ...logs]);
            setCreateDialogOpen(false);
            resetForm();
            if (acceptable) {
                toast.success('Temperature logged successfully');
            } else {
                toast.warning('Temperature logged - outside acceptable range');
            }
        } catch {
            toast.error('Failed to log temperature');
        }
    };

    const resetForm = () => {
        setItemName('');
        setLocation('');
        setTemperature('');
        setCorrectiveAction('');
        setSelectedSavedItemId('');
    };

    const handleDateSelect = (date) => {
        setSelectedDate(format(date, 'yyyy-MM-dd'));
    };

    const tempValue = parseFloat(temperature);
    const isAcceptable = !isNaN(tempValue) && location ? isTemperatureAcceptable(tempValue, location) : true;

    if (loading && !logs.length && activeTab === 'today') {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <RefreshCw className="h-8 w-8 brand-icon animate-spin" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6" data-testid="temperature-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-100">Temperature Logs</h1>
                        <p className="text-slate-400 mt-1">Record and monitor food temperatures</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {/* Thermometer Connection */}
                        {thermometer.bleSupported && (
                            thermometer.connected ? (
                                <Button variant="outline" size="sm" onClick={thermometer.disconnect}
                                    className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                                    data-testid="thermometer-disconnect">
                                    <BluetoothConnected className="h-4 w-4 mr-1" />
                                    {thermometer.deviceName} {thermometer.temperature !== null && `(${thermometer.temperature.toFixed(1)}°C)`}
                                </Button>
                            ) : (
                                <Button variant="outline" size="sm" onClick={async () => {
                                    const r = await thermometer.connect();
                                    if (r.success) toast.success(`Connected to ${r.deviceName}`);
                                    else if (r.error !== 'Pairing cancelled') toast.error(r.error);
                                }} disabled={thermometer.connecting}
                                    className="border-slate-700 text-slate-400 hover:text-blue-400 hover:border-blue-500/30"
                                    data-testid="thermometer-connect">
                                    {thermometer.connecting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Bluetooth className="h-4 w-4 mr-1" />}
                                    Thermometer
                                </Button>
                            )
                        )}

                        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                            <DialogTrigger asChild>
                                <Button className="brand-btn" data-testid="log-temperature-button">
                                    <Plus className="h-4 w-4 mr-2" /> Log Temperature
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
                                <DialogHeader>
                                    <DialogTitle className="text-slate-100">Log Temperature</DialogTitle>
                                </DialogHeader>

                                {/* Scanner / Thermometer status bar */}
                                <div className="flex gap-2 flex-wrap">
                                    <div className={cn("flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border",
                                        thermometer.connected ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" : "bg-slate-800 border-slate-700 text-slate-500"
                                    )} data-testid="dialog-thermometer-status">
                                        {thermometer.connected ? <BluetoothConnected className="h-3 w-3" /> : <BluetoothOff className="h-3 w-3" />}
                                        {thermometer.connected
                                            ? `${thermometer.deviceName}: ${thermometer.temperature?.toFixed(1) ?? '...'}°C`
                                            : 'No thermometer'}
                                    </div>
                                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border bg-blue-500/10 border-blue-500/30 text-blue-400"
                                        data-testid="dialog-scanner-status">
                                        <ScanLine className="h-3 w-3" />
                                        Scanner listening...
                                    </div>
                                </div>

                                <div className="space-y-4 pt-2">
                                    {/* Saved Item Quick Select */}
                                    {savedItems.length > 0 && (
                                        <div className="space-y-2">
                                            <Label className="text-slate-300">Quick Select (or scan barcode)</Label>
                                            <Select value={selectedSavedItemId} onValueChange={handleSavedItemSelect}>
                                                <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200" data-testid="temp-saved-item-select">
                                                    <SelectValue placeholder="Select saved item..." />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800">
                                                    {savedItems.map(item => (
                                                        <SelectItem key={item.id} value={item.id} className="text-slate-200 focus:bg-slate-800">
                                                            {item.name} ({item.location_type})
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Item Name *</Label>
                                        <Input
                                            value={itemName}
                                            onChange={(e) => setItemName(e.target.value)}
                                            placeholder="e.g., Main Fridge, Walk-in Freezer"
                                            className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600"
                                            data-testid="temp-item-name"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Location *</Label>
                                        <Select value={location} onValueChange={setLocation}>
                                            <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200" data-testid="temp-location-select">
                                                <SelectValue placeholder="Select location" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                {locations.map(loc => (
                                                    <SelectItem key={loc.id} value={loc.id} className="text-slate-200 focus:bg-slate-800">
                                                        {loc.label} ({loc.range})
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {location && (
                                            <p className="text-xs text-slate-500">
                                                Acceptable range: {getTemperatureRange(location)}
                                            </p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">
                                            Temperature (°C) *
                                            {thermometer.connected && thermometer.temperature !== null && (
                                                <span className="ml-2 text-emerald-400 text-xs font-normal">
                                                    Live: {thermometer.temperature.toFixed(1)}°C
                                                </span>
                                            )}
                                        </Label>
                                        <div className="flex gap-2">
                                            <Input
                                                type="number"
                                                step="0.1"
                                                value={temperature}
                                                onChange={(e) => setTemperature(e.target.value)}
                                                placeholder="e.g., 4.5"
                                                className={cn(
                                                    "bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 flex-1",
                                                    temperature && !isAcceptable && "border-rose-500 focus:border-rose-500"
                                                )}
                                                data-testid="temp-value-input"
                                            />
                                            {thermometer.connected && thermometer.temperature !== null && (
                                                <Button variant="outline" size="sm"
                                                    className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 shrink-0"
                                                    onClick={() => setTemperature(thermometer.temperature.toFixed(1))}
                                                    data-testid="use-thermometer-reading">
                                                    Use Reading
                                                </Button>
                                            )}
                                        </div>
                                        {temperature && location && (
                                            <p className={cn("text-xs flex items-center gap-1",
                                                isAcceptable ? "text-emerald-400" : "text-rose-400"
                                            )}>
                                                {isAcceptable ? (
                                                    <><CheckCircle2 className="h-3 w-3" /> Within acceptable range</>
                                                ) : (
                                                    <><AlertTriangle className="h-3 w-3" /> Outside acceptable range!</>
                                                )}
                                            </p>
                                        )}
                                    </div>
                                    {temperature && !isAcceptable && (
                                        <div className="space-y-2">
                                            <Label className="text-slate-300">Corrective Action Taken *</Label>
                                            <Textarea
                                                value={correctiveAction}
                                                onChange={(e) => setCorrectiveAction(e.target.value)}
                                                placeholder="Describe the corrective action taken..."
                                                className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600"
                                                data-testid="temp-corrective-action"
                                            />
                                        </div>
                                    )}
                                    <Button
                                        onClick={handleCreateLog}
                                        className="w-full brand-btn"
                                        data-testid="submit-temperature"
                                    >
                                        Log Temperature
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>

                {/* Tabs: Today / History */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="bg-slate-900 border border-slate-800">
                        <TabsTrigger value="today" className="data-[state=active]:bg-slate-800 data-[state=active]:text-[var(--brand-primary)] text-slate-400" data-testid="tab-today">
                            <Thermometer className="h-4 w-4 mr-1.5" /> Today
                        </TabsTrigger>
                        <TabsTrigger value="history" className="data-[state=active]:bg-slate-800 data-[state=active]:text-[var(--brand-primary)] text-slate-400" data-testid="tab-history">
                            <History className="h-4 w-4 mr-1.5" /> History
                        </TabsTrigger>
                    </TabsList>

                    {/* TODAY TAB */}
                    <TabsContent value="today" className="mt-4 space-y-4">
                        {/* Quick Reference */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {locations.map(loc => (
                                <Card key={loc.id} className="bg-slate-900 border-slate-800">
                                    <CardContent className="py-3 text-center">
                                        <p className="text-sm font-medium text-slate-200">{loc.label}</p>
                                        <p className="text-xs brand-text mt-0.5">{loc.range}</p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>

                        {/* Date Picker */}
                        <div className="flex justify-end">
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="temp-date-picker">
                                        <CalendarIcon className="h-4 w-4 mr-2" />
                                        {formatDate(selectedDate)}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-800" align="end">
                                    <Calendar mode="single" selected={new Date(selectedDate)} onSelect={handleDateSelect} className="text-slate-200" />
                                </PopoverContent>
                            </Popover>
                        </div>

                        {/* Temperature Logs */}
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-lg text-slate-100">Logs for {formatDate(selectedDate)}</CardTitle>
                                    <Button variant="ghost" size="sm" onClick={loadLogs} className="text-slate-400 hover:text-slate-200">
                                        <RefreshCw className="h-4 w-4" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {logs.length > 0 ? (
                                    <div className="space-y-3">
                                        {logs.map(log => (
                                            <div key={log.id} className={cn(
                                                'flex items-center justify-between p-4 rounded-lg border',
                                                log.is_acceptable ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-rose-500/5 border-rose-500/20'
                                            )} data-testid={`temp-log-${log.id}`}>
                                                <div className="flex items-center gap-4">
                                                    <div className={cn('h-12 w-12 rounded-xl flex items-center justify-center',
                                                        log.is_acceptable ? 'bg-emerald-500/10' : 'bg-rose-500/10'
                                                    )}>
                                                        {log.is_acceptable
                                                            ? <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                                                            : <AlertTriangle className="h-6 w-6 text-rose-400" />}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-slate-200">{log.item_name}</p>
                                                        <p className="text-sm text-slate-400 capitalize">
                                                            {log.location.replace('_', ' ')} &bull; {formatDateTime(log.recorded_at)}
                                                        </p>
                                                        {!log.is_acceptable && log.corrective_action && (
                                                            <p className="text-xs text-rose-300 mt-1">Action: {log.corrective_action}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className={cn('text-2xl font-bold',
                                                        log.is_acceptable ? 'text-emerald-400' : 'text-rose-400'
                                                    )}>{log.temperature}°C</p>
                                                    <p className="text-xs text-slate-500">By {log.recorded_by_name}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-12 text-slate-500">
                                        <Thermometer className="h-10 w-10 mx-auto mb-3 opacity-30" />
                                        <p className="font-medium">No temperature logs</p>
                                        <p className="text-sm mt-1">Log your first temperature reading for today</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* HISTORY TAB */}
                    <TabsContent value="history" className="mt-4 space-y-4">
                        {/* Filters */}
                        <Card className="bg-slate-900 border-slate-800">
                            <CardContent className="pt-4">
                                <div className="flex flex-col sm:flex-row gap-3 items-end">
                                    <div className="flex-1">
                                        <Label className="text-slate-300 text-xs">Start Date</Label>
                                        <Input type="date" value={historyStartDate} onChange={(e) => setHistoryStartDate(e.target.value)}
                                            className="mt-1 bg-slate-950 border-slate-800 text-slate-200" data-testid="history-start-date" />
                                    </div>
                                    <div className="flex-1">
                                        <Label className="text-slate-300 text-xs">End Date</Label>
                                        <Input type="date" value={historyEndDate} onChange={(e) => setHistoryEndDate(e.target.value)}
                                            className="mt-1 bg-slate-950 border-slate-800 text-slate-200" data-testid="history-end-date" />
                                    </div>
                                    <div className="flex-1">
                                        <Label className="text-slate-300 text-xs">Filter by Item</Label>
                                        <Select value={historyItemFilter} onValueChange={setHistoryItemFilter}>
                                            <SelectTrigger className="mt-1 bg-slate-950 border-slate-800 text-slate-200" data-testid="history-item-filter">
                                                <SelectValue placeholder="All items" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                <SelectItem value="all" className="text-slate-200 focus:bg-slate-800">All Items</SelectItem>
                                                {savedItems.map(item => (
                                                    <SelectItem key={item.id} value={item.id} className="text-slate-200 focus:bg-slate-800">
                                                        {item.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <Button onClick={loadHistory} disabled={historyLoading}
                                        className="brand-btn h-10"
                                        data-testid="load-history-button">
                                        {historyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Load'}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Summary Cards */}
                        {historyData && (
                            <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <SummaryCard label="Total Logs" value={historyData.summary.total} color="text-slate-200" />
                                    <SummaryCard label="Acceptable" value={historyData.summary.acceptable} color="text-emerald-400" />
                                    <SummaryCard label="Issues" value={historyData.summary.issues} color="text-rose-400" />
                                    <SummaryCard label="Compliance" value={`${historyData.summary.compliance_rate}%`}
                                        color={historyData.summary.compliance_rate >= 90 ? "text-emerald-400" : "text-rose-400"} />
                                </div>

                                {/* Alerts */}
                                {historyData.alerts.length > 0 && (
                                    <Card className="bg-rose-500/5 border-rose-500/20">
                                        <CardHeader>
                                            <CardTitle className="text-base text-rose-400 flex items-center gap-2">
                                                <AlertTriangle className="h-4 w-4" /> Compliance Alerts ({historyData.alerts.length})
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                                {historyData.alerts.map(alert => (
                                                    <div key={alert.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/50 border border-rose-500/10"
                                                        data-testid={`alert-${alert.id}`}>
                                                        <div>
                                                            <p className="text-sm font-medium text-slate-200">{alert.item_name}</p>
                                                            <p className="text-xs text-slate-400">{formatDateTime(alert.recorded_at)} &bull; {alert.location}</p>
                                                            {alert.corrective_action && (
                                                                <p className="text-xs text-rose-300 mt-0.5">Action: {alert.corrective_action}</p>
                                                            )}
                                                        </div>
                                                        <p className="text-lg font-bold text-rose-400">{alert.temperature}°C</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* All History Logs */}
                                <Card className="bg-slate-900 border-slate-800">
                                    <CardHeader>
                                        <CardTitle className="text-base text-slate-100">
                                            All Logs ({historyData.logs.length})
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-2 max-h-96 overflow-y-auto">
                                            {historyData.logs.map(log => (
                                                <div key={log.id} className={cn(
                                                    'flex items-center justify-between p-3 rounded-lg border',
                                                    log.is_acceptable ? 'border-slate-800' : 'border-rose-500/20 bg-rose-500/5'
                                                )}>
                                                    <div>
                                                        <p className="text-sm font-medium text-slate-200">{log.item_name}</p>
                                                        <p className="text-xs text-slate-400">
                                                            {formatDateTime(log.recorded_at)} &bull; {log.location.replace('_', ' ')} &bull; {log.recorded_by_name}
                                                        </p>
                                                    </div>
                                                    <p className={cn('text-lg font-bold',
                                                        log.is_acceptable ? 'text-emerald-400' : 'text-rose-400'
                                                    )}>{log.temperature}°C</p>
                                                </div>
                                            ))}
                                            {historyData.logs.length === 0 && (
                                                <p className="text-center text-slate-500 py-8">No logs found for this period</p>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            </>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </Layout>
    );
};

function SummaryCard({ label, value, color }) {
    return (
        <Card className="bg-slate-900 border-slate-800">
            <CardContent className="py-4 text-center">
                <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
                <p className={cn("text-2xl font-bold mt-1", color)}>{value}</p>
            </CardContent>
        </Card>
    );
}

export default Temperature;
